import {
  VuexModule,
  Module,
  Mutation,
  Action,
  MutationAction,
} from "vuex-module-decorators";
import { WebApiModule } from "@/models/WebApiModule";
import {
  ConnectionParameters,
  EventData,
  ExtraDataGroupModel,
} from "@/models/FeatureMap";
import {
  SettingsMessage,
  ConnectionEvents,
  DeviceInfoResponseLike,
} from "@thales-dis-dr/drmcl-ts-shared";

interface DIState {
  _deviceInfo: DeviceInfoResponseLike | null;
}
interface SesState {
  _session: WebApiModule | null;
}

interface SettState {
  _settings: SettingsMessage | null;
}
interface ConnState {
  _isConnected: boolean;
}

interface SendMessgeState {
  _isConnected: boolean;
}

// interface loadExtraDataResponse {
//   _extraData: ExtraDataGroupModel | null;
//   _extraDataLoading: boolean;
// }

interface State extends DIState, SesState, SettState, ConnState, SendMessgeState { }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function timeoutPromise<T>(t: any, ms = 15000): Promise<T> {
  return new Promise<T>((_, reject) => {
    setTimeout(reject.bind(t, "Connection Timed Out"), ms);
  });
}

function delay(ms?: number | undefined): Promise<void> {
  return new Promise<void>(function (resolve) {
    setTimeout(() => resolve(), ms);
  });
}

@Module({ namespaced: true })
class Webapi extends VuexModule implements State {
  _session: WebApiModule | null = null;
  _settings: SettingsMessage | null = null;
  _deviceInfo: DeviceInfoResponseLike | null = null;
  _isConnected = false;
  _events: EventData[] = [];
  _eventId = 0;
  _extraData: ExtraDataGroupModel | null = null;
  _extraDataLoading = false;

  @Mutation
  SET_SESSION_MUTATION(session: WebApiModule | null): void {
    this._session = session;
    // if (this._session != null) {
    //   this._session.once("CLOSED", () => {
    //     this.context.commit("SET_ISCONNECTED_MUTATION", false);
    //   });
    // }

    this._isConnected = !!session;

    // clear events
    while (this._events.length > 0) {
      this._events.shift();
    }
    this._eventId = 0;
  }

  @Mutation
  SET_SETTINGS_MUTATION(settings: SettingsMessage | null): void {
    this._settings = settings;
  }

  @Mutation
  SET_ISCONNECTED_MUTATION(isConnected: boolean): void {
    this._isConnected = isConnected;
  }

  @MutationAction({ mutate: ["_isConnected"] })
  closeConnection(): Promise<ConnState> {
    this._session?.end_session();
    return new Promise<ConnState>((resolve) => {
      this._session?.once("SESSION_CLOSED", () => {
        this._session?.close_connection();
        resolve({ _isConnected: false });
      });
    });
  }

  @MutationAction({ mutate: ["_settings"] })
  getReaderSettings(): Promise<SettState> {
    this._session?.send_message(
      JSON.stringify({
        t: 32,
        i: this._session.getNextI(),
      })
    );

    return new Promise<SettState>((resolve) => {
      this._session?.once(ConnectionEvents.RETURNED_SETTINGS, (data) => {
        resolve({ _settings: data });
      });
    });
  }

  // openConnection(context, payload) {
  //   this.webApi.open_connection(payload.ipaddress, payload.id, payload.key);
  // }
  @Action({ commit: "SET_SESSION_MUTATION" })
  setConnection(payload: WebApiModule): WebApiModule {
    return payload;
  }

  @Action
  send_message(payload: string | Record<string, unknown>): void {
    if (typeof payload !== "string") payload = JSON.stringify(payload);
    try {
      this._session?.send_message(payload);
    } catch (err) {
      this.context.commit("SET_ISCONNECTED_MUTATION", false);
    }
  }

  @Mutation
  SetExtraDataLoading(v: boolean): void {
    this._extraDataLoading = v;
  }

  @Mutation
  SetExtraData(v: ExtraDataGroupModel | null): void {
    this._extraData = v;
  }

  @Action
  async loadExtraData(): Promise<void> {
    this.context.commit("SetExtraDataLoading", true);
    this._session?.send_message(
      JSON.stringify({
        t: ConnectionEvents.GET_EXTRADATA,
        i: this._session.getNextI(),
      })
    );
    const d = delay(500);
    const p = new Promise<ExtraDataGroupModel>((resolve) => {
      this._session?.once(ConnectionEvents.EXTRADATA, (data) => {
        resolve(ExtraDataGroupModel.create_root(data.records));
      });
    });

    const [data] = await Promise.all([p, d]);
    this.context.commit("SetExtraData", data);
    this.context.commit("SetExtraDataLoading", false);
  }

  @MutationAction({ mutate: ["_deviceInfo"] })
  setDeviceInfo(info: DeviceInfoResponseLike | null): Promise<DIState> {
    return Promise.resolve<DIState>({
      _deviceInfo: info,
    });
  }

  @Action
  saveSettings(readerSettings: SettingsMessage): void {
    this._session?.send_message(
      JSON.stringify({
        t: 35,
        i: this._session.getNextI(),
        d: readerSettings,
      })
    );
  }

  @Mutation
  updateEvent({ id, data }: { id: number; data: Partial<EventData> }): void {
    const index = this._events.findIndex((x) => x.id == id);
    if (index >= 0) {
      const item = { ...this._events[index], ...data };
      this._events[index] = item;
    }
  }

  @Mutation
  appendEvent({ event, isError }: { event: string, isError?: boolean }): void {
    const id = this._eventId++;
    const last = this._events[this._events.length - 1];
    const now = new Date().toLocaleString();
    if (last && last.anevent === event && last.time === now) {
      return;
    }

    const index = this._events.push({
      id,
      isError,
      anevent: event,
      time: now,
      visible: true,
    });
    if (!isError) {
      setTimeout(
        () => {
          this._events[index - 1].visible = false;
          //this.updateEvent({ id, data: { visible: false } });
        },
        isError ? 6000 : 1000
      );
    }
  }

  @Mutation
  clearEvents(): void {
    while (this._events.length > 0) {
      this._events.shift();
    }
    this._eventId = 0;
  }

  @MutationAction({ mutate: ["_session"] })
  openConnection({
    webApi,
    opt,
  }: {
    webApi: WebApiModule;
    opt: ConnectionParameters;
  }): Promise<SesState> {
    // vue looses this inside the promise, so save the only object we need
    return Promise.race([
      new Promise<SesState>((resolve, reject) => {
        if (!opt) {
          return reject("Missing connection parameters");
        }
        if (!opt.ipaddress) {
          return reject("Missing Address");
        }
        if (!opt.jwt) {
          if (!opt.id) {
            return reject("Missing Id");
          }
          if (!opt.key) {
            return reject("Missing Key");
          }
        }
        if (!opt.port) opt.port = null;

        webApi.once("CONNECTED", () => {
          if (webApi != null) {
            webApi.once("CLOSED", () => {
              this.context.commit("SET_ISCONNECTED_MUTATION", false);
            });
          }
          resolve({ _session: webApi });
          return true;
        });
        webApi.once("AUTHENTICATED", () => {
          resolve({ _session: webApi });
          return true;
        });
        webApi.once("UNPROVISIONED_DEVICE", () => {
          reject("The Device is Unprovisioned");
          return true;
        });
        webApi.once("NOT_AUTHORIZED", () => {
          reject("Incorrect ID or Key!");
          return true;
        });
        webApi.once("INVALID_KEY", () => {
          reject("Incorrect ID or Key!");
          return true;
        });
        webApi.once("INTERNAL_ERROR", () => {
          reject("Reader Internal Error OccuredD");
          return true;
        });
        if (opt.jwt) {
          webApi.open_jwt_connection(opt.ipaddress, opt.jwt, opt.port);
        } else {
          webApi.open_connection(opt.ipaddress, opt.id, opt.key, opt.port);
        }
      }),
      timeoutPromise<SesState>(this, 15000),
    ]);
  }

  get extraDataLoading(): boolean {
    return this._extraDataLoading;
  }

  get extraData(): ExtraDataGroupModel | null {
    return this._extraData;
  }

  get events(): EventData[] {
    return this._events;
  }

  get errors(): EventData[] {
    return this._events;
  }

  get session(): WebApiModule | null {
    return this._session;
  }

  get readerSettings(): SettingsMessage | null {
    return this._settings;
  }

  get isConnected(): boolean {
    return this._isConnected;
  }

  get deviceInfo(): DeviceInfoResponseLike | null {
    return this._deviceInfo;
  }

  // begin test changes
  get isNotConnected(): boolean {
    return true;
  }
  // end test changes
}

export default Webapi;
